import { initialState, makeListEffects, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import { getList, update } from '@/services/global';
import { hydrateCollaboratorUsers } from '@/utils/collaborators';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

const api = bindServices('connections');
import { message } from 'antd';
import { getIntl } from 'umi';
import { queryAuthorizeConnection, queryValidateConnection } from '../service';
import { fetchConnectionOperationsList } from './service';
import {
  getDatabaseConnectionQuery,
  getDatabaseQueryParameterNames,
  shouldPromptDatabaseValidationParameters,
  toValidateConnectionBody,
} from './utils/databaseValidationParameters';
import { filtersConfig } from './utils/filtersConfig';

const { ConnectionType } = CoreEnums;
const namespace = 'connections';
const fetchRoute = 'connection';

const supportsAuthorizeFlow = (connectionType) =>
  connectionType === ConnectionType.LinkedIn ||
  connectionType === ConnectionType.AzureGraph ||
  connectionType === ConnectionType.Google;

const extractRedirectUrl = (response) => {
  if (!response) return null;
  if (typeof response === 'string') return response.trim() || null;
  if (typeof response === 'object' && response.redirectUrl) return response.redirectUrl;
  return null;
};

const validateOrAuthorizeConnection = function* (
  connectionType,
  connectionId,
  { call },
  validationParameters,
) {
  if (supportsAuthorizeFlow(connectionType)) {
    const redirectUri = `${window.location.origin}/connections/callback`;
    const response = yield call(queryAuthorizeConnection, connectionId, redirectUri);

    const redirectUrl = extractRedirectUrl(response);
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return { isRedirection: true, redirectUrl };
    }

    return { isRedirection: false };
  }

  yield call(
    queryValidateConnection,
    connectionId,
    toValidateConnectionBody(validationParameters),
  );
  return { isRedirection: false };
};

const extractErrorMessage = (error, intl) => {
  if (error?.data?.message) {
    return error.data.message;
  } else if (error?.response?.data?.message) {
    return error.response.data.message;
  } else if (error?.message) {
    return error.message;
  } else if (typeof error === 'string') {
    return error;
  } else {
    return intl.formatMessage({ id: 'component.errorBoundary.message' });
  }
};

const ConnectionsModel = {
  namespace,
  state: {
    ...initialState,
    filtersConfig,
    validatingConnection: false,
    pendingDatabaseValidation: null,
    // Collaborator slice — backs the shared <CollaboratorsDrawer />
    // when the user opens it from the legacy `/connections` page or the
    // Launchpad row-command kebab. Living in DVA state lets a single
    // drawer host serve both surfaces.
    collaborators: [],
    collaboratorsConnection: null,
  },
  effects: {
    ...makeListEffects({
      namespace,
      fetchRoute: 'connection',
      fetchParams: 'connections',
    }),
    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(update, { route: fetchRoute, ...payload });
        yield put({
          type: 'success',
          payload: { ...res, success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        const intl = getIntl();
        const errorMessage = extractErrorMessage(e, intl);

        if (errorMessage) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: errorMessage,
            },
            validationErrors: e.validationErrors,
          });

          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: errorMessage,
            },
          });
        }
      }
    },
    // Main management view (`/connections` sidebar menu) →
    // `/connection/manage`. Per the "AccessControl vs Collaborator" UX
    // guide, `/manage` is reserved for top-level management screens
    // accessed from the left sidebar; every other context (oAuth
    // selectors, action configs, process pickers) uses
    // `/connection/allowed`. `canManage` gates configuration UI on
    // each card.
    *fetchData({ payload }, { call, put, select }) {
      try {
        const pagination = yield select((state) => state[namespace].pagination);
        const pageSize = yield select((state) => state.global.pageSize);
        const baseSelect =
          CoreQueries.connections?.paged?.select ||
          'id,type,name,description,isActive,validated,isPublic';
        const res = yield call(getList, {
          route: payload.route ? payload.route : 'connection/manage',
          params: {
            pageSize,
            ...CoreQueries.connections.paged,
            select: `${baseSelect},canManage`,
            ...pagination,
            ...payload,
          },
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: pageSize, page: payload.page },
        });
      } catch (e) {}
    },
    *create({ payload }, { call, put }) {
      try {
        const { validateAfterSave, connectionId, connectionType, validationParameters, ...restPayload } =
          payload;

        const response = yield call(api.create, restPayload);

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        // Close drawer and refresh list before validation so validation errors do not keep the form open.
        yield put({
          type: 'success',
          payload: { success: true },
        });

        if (validateAfterSave && connectionId && connectionType) {
          yield put({ type: 'setValidatingConnection', payload: true });
          try {
            const validationResult = yield call(
              validateOrAuthorizeConnection,
              connectionType,
              connectionId,
              { call },
              validationParameters,
            );

            if (validationResult && validationResult.isRedirection) {
              return response;
            }

            yield put({
              type: 'global/showNotificationsGlobal',
              payload: {
                visible: true,
                type: 'success',
                message: `${intl.formatMessage({
                  id: 'component.notifications.operationCompleted',
                })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
              },
            });

            yield put({ type: 'fetchData', payload: { page: 1, limit: 10 } });
          } catch (validationError) {
            yield put({
              type: 'global/showNotificationsGlobal',
              payload: {
                visible: true,
                type: 'error',
                message: extractErrorMessage(validationError, intl),
              },
            });
          } finally {
            yield put({ type: 'setValidatingConnection', payload: false });
          }
        }

        return response;
      } catch (error) {
        const intl = getIntl();

        const errorMessage = extractErrorMessage(error, intl);

        if (errorMessage) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: errorMessage,
            },
            validationErrors: error.validationErrors,
          });

          yield put({
            type: 'errors',
            payload: {
              validationErrors: error.validationErrors || [],
              message: errorMessage,
            },
          });
        }

        return null;
      }
    },
    *fetchConnectionOperationsApiData({ payload }, { call, put }) {
      try {
        if (payload?.deeps?.length > 0) {
          yield put({ type: 'global/cleanApiData', payload: payload.deeps });
        }

        yield put({
          type: 'global/queryApiData',
          payload: { ...payload, loading: true },
        });

        const data = yield call(fetchConnectionOperationsList, payload.route, payload.params || {});

        yield put({
          type: 'global/queryApiData',
          payload: { data, ...payload, loading: false },
        });
      } catch (e) {
        yield put({
          type: 'global/queryApiData',
          payload: { ...payload, loading: false },
        });
      }
    },
    *validateConnection({ payload }, { call, put }) {
      try {
        const { connectionType, connectionId, params, validationParameters } = payload;

        if (shouldPromptDatabaseValidationParameters({ connectionType, validationParameters })) {
          let query = payload.query;
          if (!query) {
            try {
              const connection = yield call(api.getById, {
                route: 'connection',
                id: connectionId,
                params: { select: 'id,type,configurations' },
                readPriority: true,
              });
              query = getDatabaseConnectionQuery(connection);
            } catch (fetchError) {
              query = '';
            }
          }

          const paramNames = getDatabaseQueryParameterNames(query);
          if (paramNames.length) {
            yield put({
              type: 'setPendingDatabaseValidation',
              payload: {
                connectionId,
                connectionType,
                paramNames,
                params,
                query,
              },
            });
            return;
          }
        }

        const result = yield call(
          validateOrAuthorizeConnection,
          connectionType,
          connectionId,
          { call },
          validationParameters,
        );

        if (result && result.isRedirection) {
          return;
        }

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        yield put({ type: 'clearPendingDatabaseValidation' });

        // Refresh listing through the standard list effect so the
        // `validated` flag updated by the backend is reflected on the cards.
        yield put({
          type: 'fetchData',
          payload: {
            page: params?.page || 1,
            limit: params?.limit || 10,
          },
        });
      } catch (error) {
        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: extractErrorMessage(error, intl),
          },
        });
      }
    },
    *authorizeConnection({ payload }, { call }) {
      try {
        const { connectionId } = payload;

        const response = yield call(queryAuthorizeConnection, connectionId);

        if (response && response.redirectUrl) {
          window.location.href = response.redirectUrl;
        } else if (response && typeof response === 'string') {
          window.location.href = response;
        } else {
          const intl = getIntl();
          throw new Error(intl.formatMessage({ id: 'global.error.connection.noRedirectUrl' }));
        }
      } catch (error) {
        const intl = getIntl();
        message.error(intl.formatMessage({ id: 'component.errorBoundary.message' }));
      }
    },

    *createAndValidateConnection({ payload }, { call, put }) {
      try {
        const { baseData, configData, connectionType, validationParameters } = payload;

        const createPayload = {
          route: 'connection',
          data: baseData,
        };

        const createdConnection = yield call(api.create, createPayload);

        const connectionId =
          createdConnection.id || createdConnection?.data?.id || createdConnection?.result?.id;

        if (!connectionId) {
          const intl = getIntl();
          throw new Error(intl.formatMessage({ id: 'global.error.connection.noConnectionId' }));
        }

        if (configData && Object.keys(configData).length > 0) {
          const configPayload = {
            route: `connection/updateConfig/${connectionType}/${connectionId}`,
            data: configData,
          };

          yield call(api.create, configPayload);
        }

        const validationResult = yield call(
          validateOrAuthorizeConnection,
          connectionType,
          connectionId,
          { call },
          validationParameters,
        );

        if (validationResult && validationResult.isRedirection) {
          return;
        }

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        yield put({
          type: 'success',
          payload: { success: true },
        });

        yield put({ type: 'fetchData', payload: { page: 1, limit: 10 } });
      } catch (error) {
        const intl = getIntl();
        const errorMessage = extractErrorMessage(error, intl);

        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'error',
            message: errorMessage,
          },
          validationErrors: error.validationErrors,
        });

        yield put({
          type: 'errors',
          payload: {
            validationErrors: error.validationErrors || [],
            message: errorMessage,
          },
        });

        return null;
      }
    },

    *createConnectionAndConfig({ payload }, { call, put }) {
      try {
        const { baseData, configData, connectionType } = payload;

        const createPayload = {
          route: 'connection',
          data: baseData,
        };

        const createdConnection = yield call(api.create, createPayload);

        const connectionId =
          createdConnection.id || createdConnection?.data?.id || createdConnection?.result?.id;

        if (!connectionId) {
          const intl = getIntl();
          throw new Error(intl.formatMessage({ id: 'global.error.connection.noConnectionId' }));
        }

        if (configData && Object.keys(configData).length > 0) {
          const configPayload = {
            route: `connection/updateConfig/${connectionType}/${connectionId}`,
            data: configData,
          };

          yield call(api.create, configPayload);
        }

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        yield put({
          type: 'success',
          payload: { success: true },
        });
      } catch (error) {
        const intl = getIntl();
        const errorMessage = extractErrorMessage(error, intl);

        yield put({
          type: 'global/showNotificationsForm',
          payload: {
            visible: true,
            type: 'error',
            message: errorMessage,
          },
          validationErrors: error.validationErrors,
        });

        yield put({
          type: 'errors',
          payload: {
            validationErrors: error.validationErrors || [],
            message: errorMessage,
          },
        });

        return null;
      }
    },

    *processCallback({ payload }, { call, put }) {
      try {
        const { callbackData } = payload;

        if (callbackData.error) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: callbackData.errorDescription || callbackData.error,
            },
          });
          return;
        }

        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        if (!guidRegex.test(callbackData.state)) {
          throw new Error(
            getIntl().formatMessage({ id: 'global.error.connection.invalidStateFormat' }),
          );
        }

        yield call(queryValidateConnection, null, {
          code: callbackData.code,
          state: callbackData.state,
          redirectUri: callbackData.redirectUri,
        });

        const intl = getIntl();
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
            message: `${intl.formatMessage({
              id: 'component.notifications.operationCompleted',
            })} ${intl.formatMessage({ id: 'component.notifications.successfully' })}`,
          },
        });

        yield put({
          type: 'fetchData',
          payload: {
            page: 1,
            limit: 10,
          },
        });
      } catch (err) {
        const intl = getIntl();
        const errorMessage = extractErrorMessage(err, intl);

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: errorMessage,
          },
        });
      }
    },

    *fetchItemConfig({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const params = {
          select: 'id,type,configurations',
        };
        // if (payload.connectionType === ConnectionType.AzureGraph) {
        //   params.expand = { server: { select: 'name' } };
        // }
        const res = yield call(api.getById, {
          route: 'connection',
          id: payload.id,
          params,
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        console.error('Error fetching item config:', e);
      }
    },

    // ─── Collaborators (Connection domain) ───────────────────────────
    //
    // Backend contract (see "AccessControl vs Collaborator" UX guide):
    //   GET    /connection/config/collaborators/{connectionId}
    //   POST   /connection/config/collaborators/{connectionId}
    //          body: { collaboratorId }
    //   DELETE /connection/config/collaborators/{connectionId}/{collaboratorRecordId}
    //
    // Refresh contract: every mutation triggers a single inline GET
    // against the primary replica (`readPriority: true`) so the drawer
    // and the row-command kebab see the row we just POSTed/DELETEd
    // without waiting for the read replica to catch up.
    // ─────────────────────────────────────────────────────────────────

    *fetchConnectionCollaborators({ payload }, { call, put, select }) {
      try {
        const connectionId =
          payload?.connectionId || (yield select((s) => s.connections.collaboratorsConnection?.id));
        if (!connectionId) {
          yield put({ type: 'saveConnectionCollaborators', payload: [] });
          return;
        }
        const res = yield call(getList, {
          route: `connection/config/collaborators/${connectionId}`,
          ...(payload?.readPriority && { readPriority: true }),
        });
        const raw = res?.items || res?.data?.items || res || [];
        const list = Array.isArray(raw) ? raw : [];
        const hydrated = yield call(hydrateCollaboratorUsers, list);
        yield put({ type: 'saveConnectionCollaborators', payload: hydrated });
      } catch (e) {
        yield put({ type: 'saveConnectionCollaborators', payload: [] });
      }
    },

    *addConnectionCollaborator({ payload }, { call, put, select }) {
      const connectionId =
        payload?.connectionId || (yield select((s) => s.connections.collaboratorsConnection?.id));
      const collaboratorIds = Array.isArray(payload?.collaboratorIds)
        ? payload.collaboratorIds.filter(Boolean)
        : payload?.collaboratorId
        ? [payload.collaboratorId]
        : [];
      let added = 0;
      try {
        if (connectionId && collaboratorIds.length) {
          for (const collaboratorId of collaboratorIds) {
            yield call(api.create, {
              route: `connection/config/collaborators/${connectionId}`,
              data: { collaboratorId },
            });
            added += 1;
          }
          if (added) {
            yield put({
              type: 'global/showNotificationsGlobal',
              payload: { visible: true, type: 'success' },
            });
          }
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
      if (connectionId) {
        yield put.resolve({
          type: 'fetchConnectionCollaborators',
          payload: { connectionId, readPriority: true },
        });
      }
    },

    *removeConnectionCollaborator({ payload }, { call, put, select }) {
      const connectionId =
        payload?.connectionId || (yield select((s) => s.connections.collaboratorsConnection?.id));
      const ids = Array.isArray(payload?.ids)
        ? payload.ids.filter(Boolean)
        : payload?.id
        ? [payload.id]
        : [];
      let removed = 0;
      try {
        if (connectionId && ids.length) {
          for (const id of ids) {
            yield call(api.apiRoute, {
              route: `connection/config/collaborators/${connectionId}/${id}`,
              method: 'delete',
            });
            removed += 1;
          }
          if (removed) {
            yield put({
              type: 'global/showNotificationsGlobal',
              payload: { visible: true, type: 'success' },
            });
          }
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
      if (connectionId) {
        yield put.resolve({
          type: 'fetchConnectionCollaborators',
          payload: { connectionId, readPriority: true },
        });
      }
    },
  },
  reducers: {
    ...makeListReducers(namespace),
    setValidatingConnection(state, { payload }) {
      return {
        ...state,
        validatingConnection: Boolean(payload),
      };
    },
    setPendingDatabaseValidation(state, { payload }) {
      return {
        ...state,
        pendingDatabaseValidation: payload || null,
      };
    },
    clearPendingDatabaseValidation(state) {
      return {
        ...state,
        pendingDatabaseValidation: null,
      };
    },
    errors(state, { payload }) {
      return {
        ...state,
        loading: false,
        validationErrors: payload.validationErrors || [],
      };
    },
    failed(state, { payload }) {
      return {
        ...state,
        loading: false,
        validationErrors: payload.validationErrors || [],
      };
    },

    saveConnectionCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },

    // Opens the shared collaborators drawer for `connection`. The
    // saga's `select` fallback reads `collaboratorsConnection.id` so
    // we can keep the row-command kebab call sites minimal (just one
    // dispatch).
    openCollaboratorsDrawer(state, { payload }) {
      return { ...state, collaboratorsConnection: payload || null, collaborators: [] };
    },

    closeCollaboratorsDrawer(state) {
      return { ...state, collaboratorsConnection: null, collaborators: [] };
    },
  },
};

export default ConnectionsModel;
